import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  Flex,
  VStack,
  HStack,
  Progress,
  Select,
  Icon,
  Button,
  IconButton,
} from "@chakra-ui/react";
import { CalendarIcon, CloseIcon } from "@chakra-ui/icons";
import DatePicker from "react-datepicker";
import axiosInstance from "../../helpers/axiosInstance";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { FaShoppingBag } from "react-icons/fa"; // Add category icon
import moment from "moment-timezone";
import { useCurrency } from "../CurrencyExchange/CurrencyContext";
import BudgetManager from "./BudgetManager";
import Translate from "../../components/LanguageTranslation/Translate";
import { categoryIcons } from "./BudgetPlanner";

// Register the necessary Chart.js elements
ChartJS.register(ArcElement, Tooltip, Legend);

const ExpenseChart = ({
  expenses,
  setExpenses,
  categories,
  setCategories,
  selectedTripId,
  totalBudget,
  setTotalBudget,
}) => {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [aggregatedCategoryExpenses, setAggregatedCategoryExpenses] = useState(
    []
  );
  const [total, setTotal] = useState(0); // New state for total
  const { convertCurrency, currency } = useCurrency();
  // const [totalBudget, setTotalBudget] = useState(""); // Default total budget
  const [newTotalBudget, setNewTotalBudget] = useState(totalBudget); // User input for new total budget
  const [selectedDate, setSelectedDate] = useState(null);
  const [tripDetails, setTripDetails] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  // Fetch trip details including departure and return dates
  useEffect(() => {
    const fetchTripDetails = async () => {
      try {
        const response = await axiosInstance.get(`/bucket-list/${selectedTripId}`);
        const trip = response.data.data;
        if (trip && trip.trip_details) {
          setTripDetails(trip.trip_details);
        }
      } catch (error) {
        console.error("Error fetching trip details:", error);
      }
    };

    if (selectedTripId) {
      fetchTripDetails();
    }
  }, [selectedTripId]);

  // Fetch expenses for the selected trip
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${selectedTripId}/expenses`
        );
        // console.log("fetchExpenses", response.data);
        setExpenses(response.data);
      } catch (error) {
        console.error("Error fetching expenses:", error);
      }
    };

    if (selectedTripId) {
      fetchExpenses();
    }
  }, [selectedTripId]);

  // Fetch categories based on selectedTripId
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axiosInstance.get(
          `/budget_planner/${selectedTripId}/categories`
        );
        setCategories(response.data); // Set categories based on API response
        // console.log("fetchCategories", response.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    if (selectedTripId) {
      fetchCategories();
    }
  }, [selectedTripId]);

  // Aggregate expenses per category and calculate total
  useEffect(() => {
    if (!selectedCategory) {
      // Calculate total expense for each category when no category is selected
      // console.log(selectedDate);
      // console.log(moment(selectedDate).format("YYYY-MM-DD"));
      const aggregatedExpenses = categories.map((category) => {
        const categoryExpenses = expenses.find(
          (expense) => expense.category_name === category.name
        );
        const totalExpense = categoryExpenses
          ? categoryExpenses.expenses.reduce(
              (total, exp) => {
                const expenseDate = moment(exp.expense_date);
                const isInRange = !dateRange.startDate || !dateRange.endDate || 
                  expenseDate.isBetween(moment(dateRange.startDate), moment(dateRange.endDate), 'day', '[]');
                const isInSingleDate = !selectedDate || 
                  moment(exp.expense_date).format("YYYY-MM-DD") == moment(selectedDate).format("YYYY-MM-DD");
                
                return (isInRange && isInSingleDate) ? total + exp.cost : total;
              },
              0
            )
          : 0;
        return {
          expense_name: category.name,
          color: category.color,
          icon: category.icon,
          amount: totalExpense,
        };
      });

      setAggregatedCategoryExpenses(aggregatedExpenses);

      // Calculate the total amount for all categories
      const totalAmount = aggregatedExpenses.reduce(
        (acc, category) => acc + category.amount,
        0
      );
      setTotal(totalAmount); // Set the total state with the calculated total amount
    }
  }, [categories, expenses, selectedCategory, selectedDate, dateRange]);

  // Filter expenses based on selected category
  useEffect(() => {
    if (selectedCategory) {
      const categoryExpenses = expenses.find(
        (expense) => expense.category_name === selectedCategory
      );
      const icon = categories.find(
        (category) => category.name == selectedCategory
      )?.icon;
      setFilteredExpenses(
        categoryExpenses
          ? categoryExpenses.expenses
              .filter(
                (expense) => {
                  const expenseDate = moment(expense.expense_date);
                  const isInRange = !dateRange.startDate || !dateRange.endDate || 
                    expenseDate.isBetween(moment(dateRange.startDate), moment(dateRange.endDate), 'day', '[]');
                  const isInSingleDate = !selectedDate || 
                    moment(expense.expense_date).format("YYYY-MM-DD") == moment(selectedDate).format("YYYY-MM-DD");
                  
                  return isInRange && isInSingleDate;
                }
              )
              .map((obj) => ({
                ...obj,
                icon: icon,
              }))
          : []
      );
    } else {
      setFilteredExpenses(aggregatedCategoryExpenses);
      // console.log(aggregatedCategoryExpenses);
    }
  }, [selectedCategory, aggregatedCategoryExpenses, expenses, selectedDate, dateRange]);

  // Chart Data
  const chartData = {
    labels: filteredExpenses.map((expense) => expense.expense_name),
    datasets: [
      {
        data: filteredExpenses.map((expense) =>
          selectedCategory ? expense.cost : expense.amount
        ),
        backgroundColor: filteredExpenses.map((expense) =>
          expense.color ? expense.color : "#000000"
        ),
        hoverBackgroundColor: filteredExpenses.map((expense) =>
          expense.color ? expense.color : "#000000"
        ),
      },
    ],
  };

  const showTotal = () => {
    if (!selectedCategory) {
      return convertCurrency(total, "USD") &&
        !isNaN(convertCurrency(total, "USD"))
        ? Number(convertCurrency(total, "USD")).toFixed(2)
        : "0.00";
    }
    const categoryTotal = filteredExpenses.reduce(
      (acc, expense) => acc + expense.cost,
      0
    );
    return convertCurrency(categoryTotal, "USD") &&
      !isNaN(convertCurrency(categoryTotal, "USD"))
      ? Number(convertCurrency(categoryTotal, "USD")).toFixed(2)
      : "0.00";
  };

  const handleTotalBudgetChange = (e) => {
    setNewTotalBudget(e.target.value);
  };

  const handleDateChange = (date) => {
    // Store only the date part, ignoring the time
    //const selectedDate = moment(date).format("YYYY-MM-DD");
    setSelectedDate(date);
  };

  const clearDate = () => {
    setSelectedDate(null);
  };

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setDateRange({ startDate: start, endDate: end });
  };

  const clearDateRange = () => {
    setDateRange({ startDate: null, endDate: null });
  };

  // Helper function to check if a date is within selected range
  const isDateInSelectedRange = (date) => {
    if (!dateRange.startDate || !dateRange.endDate) return true;
    const checkDate = moment(date);
    const startDate = moment(dateRange.startDate);
    const endDate = moment(dateRange.endDate);
    return checkDate.isBetween(startDate, endDate, 'day', '[]'); // inclusive
  };

  // Helper function to format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("DD MMM YYYY");
  };

  // Helper function to check if a date is within trip range
  const isDateInTripRange = (date) => {
    if (!tripDetails?.departure_date || !tripDetails?.return_date) return true;
    const checkDate = moment(date);
    const departureDate = moment(tripDetails.departure_date);
    const returnDate = moment(tripDetails.return_date);
    return checkDate.isBetween(departureDate, returnDate, 'day', '[]'); // inclusive
  };

  // Helper function to highlight trip dates in calendar
  const highlightTripDates = (date) => {
    if (!tripDetails?.departure_date || !tripDetails?.return_date) return "";
    
    const checkDate = moment(date);
    const departureDate = moment(tripDetails.departure_date);
    const returnDate = moment(tripDetails.return_date);
    const selectedMoment = selectedDate ? moment(selectedDate) : null;
    
    // Check if this is within the selected date range
    if (dateRange.startDate && dateRange.endDate) {
      const rangeStart = moment(dateRange.startDate);
      const rangeEnd = moment(dateRange.endDate);
      
      if (checkDate.isSame(rangeStart, 'day') || checkDate.isSame(rangeEnd, 'day') || 
          checkDate.isBetween(rangeStart, rangeEnd, 'day', '()')) {
        return "selected-range-date";
      }
    }
    
    // Check if this is the selected date (for single date selection)
    if (selectedMoment && checkDate.isSame(selectedMoment, 'day')) {
      return "selected-date";
    }
    
    if (checkDate.isSame(departureDate, 'day')) {
      return "trip-duration-date";
    }
    
    if (checkDate.isSame(returnDate, 'day')) {
      return "trip-duration-date";
    }
    
    if (checkDate.isBetween(departureDate, returnDate, 'day', '()')) {
      return "trip-duration-date";
    }
    
    return "";
  };

  return (
    <Box
      w={{ base: "100%", md: "80%", lg: "75%" }}
      p={{ base: 0, md: 2, lg: 2 }}
      bg="#FAFDFC"
      borderRadius="lg"
      shadow="lg"
      h={{ base: "auto", md: "100vh", lg: "100vh" }}
      overflowY={{ base: "none", md: "auto", lg: "auto" }}
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        // Date picker styles
        "& .selected-date": {
          backgroundColor: "#38a169 !important",
          color: "white !important",
          borderRadius: "50% !important",
          fontWeight: "bold !important"
        },
        "& .selected-range-date": {
          backgroundColor: "#000000 !important",
          color: "white !important",
          borderRadius: "50% !important",
          fontWeight: "bold !important"
        },
        "& .trip-duration-date": {
          backgroundColor: "#286328 !important",
          color: "white !important",
          borderRadius: "50% !important",
        }
      }}
    >
      <VStack spacing={{ base: 2, md: 4, lg: 4 }} align="stretch">
        <BudgetManager
          total={total}
          currency={currency}
          convertCurrency={convertCurrency}
          selectedTripId={selectedTripId}
          totalBudget={totalBudget}
          setTotalBudget={setTotalBudget}
        />

        <Box
          bg="white"
          p={4}
          borderRadius="xl"
          //   h="75vh"
        >
          {/* Category and Expense Select Section */}
          <HStack justify="space-between" w="100%" mt={4}>
            <Select
              placeholder={Translate({
                text: "All Categories",
                as: "string",
              })}
              size="sm"
              variant="filled"
              bg="#FAFDFC"
              _hover="none"
              borderRadius="md"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {/* <option value="">All Categories</option> */}
              {categories.map((category, index) => (
                <option key={index} value={category.name}>
                  <Translate text={category.name} />
                </option>
              ))}
            </Select>

            <VStack spacing={2} w="40%">
              {/* Single Date Picker */}
              {/* <HStack w="100%">
                <DatePicker
                  selected={selectedDate}
                  onChange={handleDateChange}
                  dateFormat="dd MMM yyyy"
                  dayClassName={highlightTripDates}
                  placeholderText="Select date"
                  customInput={
                    <Button
                      leftIcon={<CalendarIcon />}
                      variant="outline"
                      w="100%"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      noOfLines={1}
                      fontSize="sm"
                    >
                      <Translate
                        text={
                          selectedDate
                            ? moment(selectedDate).format("DD MMM YYYY") // Show selected date
                            : "Select date"
                        }
                      />
                    </Button>
                  }
                />

                <IconButton
                  icon={<CloseIcon />}
                  size="sm"
                  onClick={clearDate}
                  aria-label="Clear date"
                />
              </HStack> */}

              {/* Date Range Picker */}
              <HStack w="100%">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange.startDate}
                  endDate={dateRange.endDate}
                  onChange={handleDateRangeChange}
                  dateFormat="dd MMM yyyy"
                  dayClassName={highlightTripDates}
                  placeholderText="Select date range"
                  customInput={
                    <Button
                      leftIcon={<CalendarIcon />}
                      variant="outline"
                      w="100%"
                      whiteSpace="nowrap"
                      textOverflow="ellipsis"
                      noOfLines={1}
                      fontSize="sm"
                    >
                      <Translate
                        text={
                          dateRange.startDate && dateRange.endDate
                            ? `${moment(dateRange.startDate).format("DD MMM")} - ${moment(dateRange.endDate).format("DD MMM YYYY")}`
                            : "Select date range"
                        }
                      />
                    </Button>
                  }
                />

                {/* Clear Button for Date Range */}
                {dateRange.startDate && dateRange.endDate && (
                  <IconButton
                    icon={<CloseIcon />}
                    size="sm"
                    onClick={clearDateRange}
                    aria-label="Clear date range"
                  />
                )}
              </HStack>
            </VStack>
          </HStack>

          {/* Chart and Legend Section */}
          <Flex justify="center" align="center" w="100%" mt={6}>
            {/* Chart Section */}
            <Box w="30%" mr={8}>
              <Doughnut
                data={chartData}
                options={{
                  cutout: "70%",
                  plugins: {
                    legend: {
                      display: false, // Disable default legend
                    },
                  },
                }}
              />
            </Box>

            {/* Custom Legend */}
            <VStack spacing={3} align="start">
              {chartData.labels.map((label, index) => (
                <HStack key={index}>
                  <Box
                    w="12px"
                    h="12px"
                    bg={chartData.datasets[0].backgroundColor[index]}
                    borderRadius="full"
                  />
                  <Text mb={0}>
                    <Translate text={label} />
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Flex>

          {/* Categories Section (Under the Chart) */}
          <VStack w="100%" align="stretch" mt={6} spacing={3}>
            {filteredExpenses.map((expense, index) => {
              const convertedCost = parseFloat(
                convertCurrency(expense.cost, "USD")
              );
              const convertedAmount = parseFloat(
                convertCurrency(expense.amount, "USD")
              );
              const convertedTotalAmount = parseFloat(
                convertCurrency(total, "USD")
              );
              return (
                <Flex
                  justify="space-between"
                  key={index}
                  borderBottom="1px"
                  borderColor="blackAlpha.100"
                  pb={2}
                >
                  <HStack spacing={3}>
                    <Box
                      w="40px"
                      h="40px"
                      bg={expense.color || "#000000"}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="full"
                    >
                      <Icon
                        as={categoryIcons[expense.icon]?.icon || FaShoppingBag}
                        color="white"
                        boxSize={5}
                      />
                    </Box>
                    <Text fontSize="md" fontWeight="bold" ml={2} mb={0}>
                      <Translate text={expense.expense_name} />
                    </Text>
                  </HStack>

                  <Text fontWeight="bold">
                    {currency}{" "}
                    {selectedCategory
                      ? convertedCost
                        ? convertedCost.toFixed(2)
                        : "0.00"
                      : convertedAmount
                      ? convertedAmount.toFixed(2)
                      : "0.00"}
                  </Text>
                </Flex>
              );
            })}

            {/* Total Amount Spent */}
            <HStack justify="right" pt={2}>
              <Text fontWeight="bold">
                <Translate text="Total" />
              </Text>
              <Text fontWeight="bold">=</Text>
              <Text fontWeight="bold">
                {currency} {showTotal()}
              </Text>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default ExpenseChart;
